import { useEffect, useRef } from 'react'
import { useAccountType } from '@/hooks/useAccountType'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { ApiError } from '@/lib/api/api-client'
import { assistantApi } from '@/lib/api/assistant.api'
import { captureRequestScope } from '@/lib/api/request-scope'
import {
  readRecordSession,
  rememberRecordSession,
} from '@/lib/benny-record-sessions'
import { setHeroHandoff } from '@/pages/simple/_components/hero-handoff'
import { useBennyStore } from '@/store/benny.store'
import { useOrganizationStore } from '@/store/organization.store'
import {
  captureActivePageSnapshot,
  usePageSnapshotStore,
} from '@/store/page-snapshot.store'
import { getOrgPath } from '@/utils/org-paths'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { SessionAnchor } from '@scrambl/shared'

/** Open over the current page; reuse Conversations when already there. Never auto-sends. */
export function useOpenBenny() {
  const location = useLocation()
  const { pathname } = location
  const navigate = useNavigate()
  const { checkFeatureFlag, isLoading } = useFeatureFlags(['SIMPLE_MODE'])
  const { isPersonal } = useAccountType()
  const slug = useOrganizationStore(state => state.currentOrganization?.slug)
  const sequence = useRef(0)
  useEffect(
    () => () => {
      sequence.current += 1
    },
    [],
  )

  return async (prompt?: string, anchor?: SessionAnchor) => {
    const simpleMode =
      !isLoading &&
      checkFeatureFlag('SIMPLE_MODE') &&
      !isPersonal &&
      Boolean(slug)
    const onConversationsPage = /\/conversations\/?$/.test(pathname)
    if (simpleMode && prompt === undefined && !anchor && onConversationsPage) {
      document
        .querySelector<HTMLTextAreaElement>('[data-benny-composer]')
        ?.focus()
      return
    }
    const request = ++sequence.current
    const owner = captureRequestScope()
    const scope = usePageSnapshotStore.getState().activeScope
    let pageSnapshot: ReturnType<typeof captureActivePageSnapshot>
    try {
      pageSnapshot = captureActivePageSnapshot()
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not read the current page details.',
      )
      return
    }
    const record = anchor ?? pageSnapshot?.record
    let sessionId = readRecordSession(owner, record)
    if (sessionId) {
      try {
        await assistantApi.getSession(sessionId)
      } catch (error) {
        if (owner.signal.aborted || request !== sequence.current) return
        if (
          error instanceof ApiError &&
          (error.status === 404 || error.status === 403)
        ) {
          rememberRecordSession(owner, record, null)
          sessionId = undefined
        } else {
          toast.error(
            'Could not open this record’s conversation. Please try again.',
          )
          return
        }
      }
    }
    if (
      owner.signal.aborted ||
      request !== sequence.current ||
      scope?.key !== usePageSnapshotStore.getState().activeScope?.key
    )
      return
    if (simpleMode && slug && onConversationsPage) {
      setHeroHandoff({
        prefill: prompt,
        anchor: record,
        pageSnapshot,
        sessionId,
      })
      navigate(getOrgPath(slug, '/conversations'), {
        viewTransition: true,
        state: location.state,
      })
    } else {
      const benny = useBennyStore.getState()
      if (!benny.open) benny.setMode('popover')
      if (record) benny.openAbout(record, prompt, sessionId)
      else if (prompt !== undefined) benny.openWithPrompt(prompt)
      else benny.setOpen(true)
    }
  }
}
